// store/jobSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';

// Очереди страницы документа — у каждого slotId своя независимая копия этих job'ов.
// Поэтому они не лежат в общем jobs, а в documentJobs[slotId].
export type DocumentJobType = 'parse' | 'paraphrase' | 'attachReport';

// Очереди страницы проверок — глобальные, по userId/reportId, не привязаны к rewrite-слоту.
export type ReportsJobType =
  | 'antiplagiat'
  | 'prepareAntiplagiatDoc'
  | 'attachAntiplagiatReport';

export type JobType = DocumentJobType | ReportsJobType;
export type JobStatus = 'idle' | 'running' | 'succeeded' | 'failed';

export interface JobInfo {
  jobId: string | null;
  status: JobStatus;
  progress: number;
  error: string | null;
  /** Машиночитаемый код ошибки парсинга/сборки (PARSER_ERRORS_FRONTEND.md §4). */
  errorCode: string | null;
  /** Можно ли предложить «Повторить» без перезагрузки DOCX (§1.4). */
  recoverable: boolean | null;
  /** Диагностика из job-failed.details / errorDetails. */
  errorDetails: any | null;
  result: any | null;
}

export type DocumentJobBundle = Record<DocumentJobType, JobInfo>;

export interface JobState {
  /** Reports-page jobs (single global set). */
  jobs: Record<ReportsJobType, JobInfo>;
  /** Document-page jobs, keyed by slotId. */
  documentJobs: Record<string, DocumentJobBundle>;
}

export const EMPTY_JOB_INFO: JobInfo = {
  jobId: null,
  status: 'idle',
  progress: 0,
  error: null,
  errorCode: null,
  recoverable: null,
  errorDetails: null,
  result: null,
};

export const EMPTY_DOC_BUNDLE: DocumentJobBundle = {
  parse: { ...EMPTY_JOB_INFO },
  paraphrase: { ...EMPTY_JOB_INFO },
  attachReport: { ...EMPTY_JOB_INFO },
};

const mkJob = (): JobInfo => ({ ...EMPTY_JOB_INFO });
const mkBundle = (): DocumentJobBundle => ({
  parse: mkJob(),
  paraphrase: mkJob(),
  attachReport: mkJob(),
});

const initialState: JobState = {
  jobs: {
    antiplagiat: mkJob(),
    prepareAntiplagiatDoc: mkJob(),
    attachAntiplagiatReport: mkJob(),
  },
  documentJobs: {},
};

const ensureBundle = (state: JobState, slotId: string): DocumentJobBundle => {
  if (!state.documentJobs[slotId]) state.documentJobs[slotId] = mkBundle();
  return state.documentJobs[slotId];
};

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {
    // ---------- Reports jobs (global) ----------
    jobStarted: (
      state,
      action: PayloadAction<{ type: ReportsJobType; jobId: string; result?: any }>
    ) => {
      const { type, jobId, result } = action.payload;
      state.jobs[type] = {
        ...state.jobs[type],
        jobId,
        status: 'running',
        progress: 0,
        error: null,
        errorCode: null,
        recoverable: null,
        errorDetails: null,
        result: result ?? null,
      };
    },
    jobProgress: (
      state,
      action: PayloadAction<{ type: ReportsJobType; progress: number }>
    ) => {
      const { type, progress } = action.payload;
      const cur = state.jobs[type];
      state.jobs[type] = {
        ...cur,
        progress: Math.max(cur.progress, Math.min(100, progress)),
      };
    },
    jobDone: (state, action: PayloadAction<{ type: ReportsJobType; result: any }>) => {
      const { type, result } = action.payload;
      state.jobs[type] = {
        ...state.jobs[type],
        status: 'succeeded',
        progress: 100,
        result,
      };
    },
    jobFailed: (
      state,
      action: PayloadAction<{
        type: ReportsJobType;
        error: string;
        errorCode?: string | null;
        recoverable?: boolean | null;
        errorDetails?: any;
      }>
    ) => {
      const { type, error, errorCode, recoverable, errorDetails } = action.payload;
      state.jobs[type] = {
        ...state.jobs[type],
        status: 'failed',
        error,
        errorCode: errorCode ?? null,
        recoverable: recoverable ?? null,
        errorDetails: errorDetails ?? null,
      };
    },
    jobReset: (state, action: PayloadAction<{ type?: ReportsJobType } | undefined>) => {
      if (action?.payload?.type) {
        state.jobs[action.payload.type] = mkJob();
      } else {
        state.jobs = {
          antiplagiat: mkJob(),
          prepareAntiplagiatDoc: mkJob(),
          attachAntiplagiatReport: mkJob(),
        };
      }
    },

    // ---------- Document jobs (per-slot) ----------
    documentJobStarted: (
      state,
      action: PayloadAction<{
        slotId: string;
        type: DocumentJobType;
        jobId: string;
        result?: any;
      }>
    ) => {
      const { slotId, type, jobId, result } = action.payload;
      const bundle = ensureBundle(state, slotId);
      bundle[type] = {
        ...bundle[type],
        jobId,
        status: 'running',
        progress: 0,
        error: null,
        errorCode: null,
        recoverable: null,
        errorDetails: null,
        result: result ?? null,
      };
    },
    documentJobProgress: (
      state,
      action: PayloadAction<{ slotId: string; type: DocumentJobType; progress: number }>
    ) => {
      const { slotId, type, progress } = action.payload;
      const bundle = ensureBundle(state, slotId);
      const cur = bundle[type];
      bundle[type] = {
        ...cur,
        progress: Math.max(cur.progress, Math.min(100, progress)),
      };
    },
    documentJobDone: (
      state,
      action: PayloadAction<{ slotId: string; type: DocumentJobType; result: any }>
    ) => {
      const { slotId, type, result } = action.payload;
      const bundle = ensureBundle(state, slotId);
      bundle[type] = { ...bundle[type], status: 'succeeded', progress: 100, result };
    },
    documentJobFailed: (
      state,
      action: PayloadAction<{
        slotId: string;
        type: DocumentJobType;
        error: string;
        errorCode?: string | null;
        recoverable?: boolean | null;
        errorDetails?: any;
      }>
    ) => {
      const { slotId, type, error, errorCode, recoverable, errorDetails } =
        action.payload;
      const bundle = ensureBundle(state, slotId);
      bundle[type] = {
        ...bundle[type],
        status: 'failed',
        error,
        errorCode: errorCode ?? null,
        recoverable: recoverable ?? null,
        errorDetails: errorDetails ?? null,
      };
    },
    documentJobReset: (
      state,
      action: PayloadAction<{ slotId: string; type?: DocumentJobType }>
    ) => {
      const { slotId, type } = action.payload;
      if (!state.documentJobs[slotId]) return;
      if (type) {
        state.documentJobs[slotId][type] = mkJob();
      } else {
        state.documentJobs[slotId] = mkBundle();
      }
    },
    /** Полностью удаляет записи о джобах для слота (при удалении слота). */
    clearSlotDocumentJobs: (state, action: PayloadAction<string>) => {
      delete state.documentJobs[action.payload];
    },
    /** Полный сброс всех per-slot документов (например, при logout). */
    clearAllDocumentJobs: (state) => {
      state.documentJobs = {};
    },
  },
});

export const {
  jobStarted,
  jobProgress,
  jobDone,
  jobFailed,
  jobReset,
  documentJobStarted,
  documentJobProgress,
  documentJobDone,
  documentJobFailed,
  documentJobReset,
  clearSlotDocumentJobs,
  clearAllDocumentJobs,
} = jobSlice.actions;

// ---------- Selectors ----------
export const selectReportsJobs = (s: RootState) => s.job.jobs;
export const selectReportsJobByType = (type: ReportsJobType) => (s: RootState) =>
  s.job.jobs[type];

/** Все per-slot бандлы. Используется для индикаторов в SlotSwitcher. */
export const selectAllDocumentJobs = (s: RootState) => s.job.documentJobs;

/** Бандл {parse,paraphrase,attachReport} для конкретного слота. */
export const selectDocumentJobsForSlot =
  (slotId: string | null | undefined) =>
  (s: RootState): DocumentJobBundle =>
    (slotId && s.job.documentJobs[slotId]) || EMPTY_DOC_BUNDLE;

/** Обратная совместимость: плоский объект jobs (только reports-очереди). */
export const selectJobs = (s: RootState) => s.job.jobs;
export const selectJobByType = (type: ReportsJobType) => (s: RootState) =>
  s.job.jobs[type];

export default jobSlice.reducer;
